import type { TFunction } from "i18next";
import { WIDGET_CHART_TYPES } from "./chartTypeRegistry";

export type ChatOutputType = "text" | "html" | "table" | "chart" | "json" | "widget";

/** Widget `output.format` values sent to WidgetBuilder via outputPreference. */
export type WidgetOutputFormat = "table" | "echarts" | "text" | "html";

export interface ChatOutputPreference {
  outputType: ChatOutputType;
  /** Set when outputType is widget — maps to widgetDraft.output.format */
  widgetFormat?: WidgetOutputFormat;
  chartType?: string;
}

export interface ChatSlashCommand {
  id: ChatOutputType;
  labelKey: string;
  descriptionKey: string;
  pickChartType?: boolean;
  pickWidgetFormat?: boolean;
}

export interface WidgetFormatOption {
  id: WidgetOutputFormat;
  labelKey: string;
  descriptionKey: string;
  pickChartType?: boolean;
}

export const CHAT_SLASH_COMMANDS: ChatSlashCommand[] = [
  { id: "text", labelKey: "chat.slash.text", descriptionKey: "chat.slash.textDesc" },
  { id: "html", labelKey: "chat.slash.html", descriptionKey: "chat.slash.htmlDesc" },
  { id: "table", labelKey: "chat.slash.table", descriptionKey: "chat.slash.tableDesc" },
  { id: "chart", labelKey: "chat.slash.chart", descriptionKey: "chat.slash.chartDesc", pickChartType: true },
  { id: "json", labelKey: "chat.slash.json", descriptionKey: "chat.slash.jsonDesc" },
  {
    id: "widget",
    labelKey: "chat.slash.widget",
    descriptionKey: "chat.slash.widgetDesc",
    pickWidgetFormat: true,
  },
];

export const WIDGET_FORMAT_OPTIONS: WidgetFormatOption[] = [
  {
    id: "table",
    labelKey: "chat.slash.widgetFormat.table",
    descriptionKey: "chat.slash.widgetFormat.tableDesc",
  },
  {
    id: "echarts",
    labelKey: "chat.slash.widgetFormat.chart",
    descriptionKey: "chat.slash.widgetFormat.chartDesc",
    pickChartType: true,
  },
  {
    id: "text",
    labelKey: "chat.slash.widgetFormat.text",
    descriptionKey: "chat.slash.widgetFormat.textDesc",
  },
  {
    id: "html",
    labelKey: "chat.slash.widgetFormat.html",
    descriptionKey: "chat.slash.widgetFormat.htmlDesc",
  },
];

export function filterSlashCommands(query: string): ChatSlashCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return CHAT_SLASH_COMMANDS;
  return CHAT_SLASH_COMMANDS.filter((cmd) => cmd.id.includes(q));
}

export function filterWidgetFormats(query: string): WidgetFormatOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return WIDGET_FORMAT_OPTIONS;
  return WIDGET_FORMAT_OPTIONS.filter((opt) => opt.id.includes(q));
}

export function widgetFormatLabel(format: WidgetOutputFormat, t: TFunction): string {
  const key = `chat.slash.widgetFormat.${format === "echarts" ? "chart" : format}`;
  return t(key, { defaultValue: format });
}

export function chartTypeLabel(chartType: string, t: TFunction, locale?: string): string {
  const normalized = chartType.trim().toLowerCase();
  const def = WIDGET_CHART_TYPES.find((c) => c.id.toLowerCase() === normalized);
  if (!def) return chartType;
  const useTr = locale?.toLowerCase().startsWith("tr");
  return useTr ? def.labelTr : def.labelEn;
}

export function outputPreferenceLabel(pref: ChatOutputPreference, t: TFunction, locale?: string): string {
  const base = t(`chat.slash.${pref.outputType}`, { defaultValue: pref.outputType });
  if (pref.outputType === "widget") {
    if (!pref.widgetFormat) return base;
    const parts = [base, widgetFormatLabel(pref.widgetFormat, t)];
    if (pref.widgetFormat === "echarts" && pref.chartType) {
      parts.push(chartTypeLabel(pref.chartType, t, locale));
    }
    return parts.join(" · ");
  }
  if (pref.chartType && pref.outputType === "chart") {
    return `${base} · ${chartTypeLabel(pref.chartType, t, locale)}`;
  }
  return base;
}

export function isChartTypeRequired(pref: ChatOutputPreference | null): boolean {
  if (pref?.outputType === "chart") return !pref.chartType;
  if (pref?.outputType === "widget") return pref.widgetFormat === "echarts" && !pref.chartType;
  return false;
}

export function isWidgetFormatRequired(pref: ChatOutputPreference | null): boolean {
  return pref?.outputType === "widget" && !pref.widgetFormat;
}

export function preferenceToApiBody(
  pref: ChatOutputPreference | null
): { outputType: string; widgetFormat?: string; chartType?: string } | undefined {
  if (!pref) return undefined;
  const body: { outputType: string; widgetFormat?: string; chartType?: string } = {
    outputType: pref.outputType,
  };
  if (pref.widgetFormat) body.widgetFormat = pref.widgetFormat;
  if (pref.chartType) body.chartType = pref.chartType;
  return body;
}
